import React, { useCallback, useEffect, useState } from 'react';
import { getCardLinkMessages, sendCardLinkMessage } from '../../api/system/system.api';

/**
 * 카드 등록 링크 **LMS 로 바로 보내기**(2026-10-19 사용자 요구) — 고객사 상세 '결제 수단' 안에 붙는다.
 *
 * - 결제 예정 안내 LMS 와 같은 경로·발신번호로 보낸다. 알림톡은 템플릿 심사 뒤 별도 단계다.
 * - 받는 사람은 **서버가** 채널 Owner 계정으로 정한다. 화면은 마스킹 번호를 보여주기만 한다.
 * - 링크는 **살아 있는 것을 다시 쓴다** — 위 '링크 만들기' 로 보여준 링크가 있으면 그 링크가 나간다.
 * - 발송 기록은 채널 이력(타임라인)에 남는다. 여기는 최근 5건만 보여준다.
 *
 * 🔴 `key={channelId}` 로 붙인다 — 채널을 바꾸면 패널이 새로 만들어져 앞 채널의 번호·버튼이 남지 않는다.
 * `[MESSAGE_SEND_RISK]` 실제 고객에게 문자가 나간다. 로컬 서버도 운영 notification 에 붙어 있으면 실제로 간다.
 */
const CardLinkSendPanel = ({ channelId, onSent, onMessage, onError }) => {
  const [overview, setOverview] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    setLoadErr('');
    try {
      const res = await getCardLinkMessages(channelId);
      setOverview(res?.data ?? null);
    } catch (e) {
      setOverview(null);
      setLoadErr(e?.message ?? '발송 정보를 불러오지 못했습니다.');
    }
  }, [channelId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!overview) return;
    const warn = `카드 등록 링크를 고객에게 LMS(문자)로 실제 발송합니다.\n\n` +
      `· 받는 사람: ${overview.storeName} (계정 ${overview.ownerAccountId}, ${overview.phoneMasked})\n` +
      `· 살아 있는 링크가 있으면 그 링크를, 없거나 3일 안에 만료되면 새 링크를 보냅니다(새로 만들면 앞 링크는 잠깁니다).\n` +
      (overview.recentlySent ? `\n⚠️ 24시간 안에 이미 보낸 기록이 있습니다. 다시 보내면 고객이 같은 안내를 또 받습니다.\n` : '') +
      `\n보낼까요?`;
    if (!window.confirm(warn)) return;

    setSending(true);
    try {
      const result = (await sendCardLinkMessage(channelId, { resend: !!overview.recentlySent }))?.data ?? null;
      await load();
      onSent?.();
      if (!result) onError?.('발송 결과를 받지 못했습니다. 이력을 확인해주세요.');
      else if (result.accepted) onMessage?.(`카드 등록 링크 LMS 접수 성공 (${result.phoneMasked}, 링크 ${result.linkExpiresAt} 까지)`);
      else onError?.(`LMS 접수 실패: ${result.resultCode}${result.resultMessage ? ` · ${result.resultMessage}` : ''}`);
    } catch (e) {
      onError?.(e?.message ?? '발송 실패');
      await load();
    } finally {
      setSending(false);
    }
  };

  if (loadErr) {
    return (
      <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
        카드 등록 링크 발송 정보를 불러오지 못했습니다: {loadErr}
        <button onClick={load} className="ml-2 underline">다시 시도</button>
      </div>
    );
  }
  if (!overview) return <div className="mb-3 text-xs text-gray-400">카드 등록 링크 발송 정보를 불러오는 중…</div>;

  const blocked = overview.problems.length > 0 || overview.quietHours || sending;

  return (
    <div className="mb-3 rounded border border-gray-200 p-3 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-gray-600">
          <b className="text-gray-800">카드 등록 링크 LMS 보내기</b>
          <span className="ml-2">받는 번호 <b className="font-mono">{overview.phoneMasked ?? '—'}</b></span>
        </div>
        <button
          onClick={send}
          disabled={blocked}
          className="px-2 py-1 rounded border bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200"
        >
          {sending ? '보내는 중…' : 'LMS 보내기'}
        </button>
      </div>

      {overview.problems.length > 0 && (
        <div className="mt-2 text-red-600">보낼 수 없습니다: {overview.problems.join(' · ')}</div>
      )}
      {overview.quietHours && (
        <div className="mt-2 text-amber-700">야간(21:00~08:00)에는 보내지 않습니다.</div>
      )}

      <button onClick={() => setShowPreview((v) => !v)} className="mt-2 text-gray-500 underline">
        {showPreview ? '문구 닫기' : '문구 보기'}
      </button>
      {showPreview && (
        <div className="mt-1 rounded bg-gray-50 border border-gray-200 p-2 whitespace-pre-wrap text-gray-700">
          <div className="font-semibold mb-1">{overview.lmsSubject}</div>
          {overview.lmsPreview}
        </div>
      )}

      {overview.recent.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-gray-500">
          {overview.recent.map((r) => (
            <li key={r.id}>
              <span className="font-mono">{r.occurredAt}</span> · {r.actorId ?? r.actorLabel} · {r.summary}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-1 text-gray-400">
        LMS 는 지금 <b>접수 결과</b>까지만 알 수 있습니다(실제 수신 여부는 아직 조회하지 않습니다).
      </div>
    </div>
  );
};

export default CardLinkSendPanel;
